/**
 * Search — progressive enhancement for sections/search.liquid.
 *
 * Everything the page does works without this file: the field is a GET form,
 * every tab is a link to a real `type` URL, and load more is a link to the next
 * paginated page. With this script the same three interactions become Section
 * Rendering API fetches, and typing refines the results in place.
 *
 * The search field is never swapped, so the caret and any composition state
 * survive a refine. Only the results, the tab row and the result meta are
 * replaced — the meta carries `role="status"`, so a screen reader hears the new
 * count without a separate live region.
 */

import {isStrictQuery, looseQuery, strictQuery} from './component-search-query.js'; // eslint-disable-line import/extensions

const SWAP_TARGETS = ['[data-search-results]', '[data-search-tabs]'];

/* Which collection holds the results depends on the tab, so appended pages go
   into whichever of these is present rather than an assumed one. */
const RESULT_CONTAINERS = ['.search__grid', '.search__cards', '.search__articles'];

/* Exact per-type totals, which Liquid cannot get in one pass — see
   templates/search.count.liquid. */
const COUNT_TYPES = ['product', 'page', 'article'];
const COUNT_TOKEN = '%COUNT%';
const RANGE_TOKEN = '%SHOWN%';
const MIN_QUERY_LENGTH = 2;

/* Set on a query rerun as typed after its quoted form found nothing. Shopify
   ignores it; this file reads it so it never re-quotes that query. */
const LOOSE_PARAM = 'loose';

/* How a render should touch session history. A tab is a navigation and earns a
   history entry; a refine or an append rewrites the current one. */
const HISTORY_PUSH = 'push';
const HISTORY_REPLACE = 'replace';
const HISTORY_KEEP = 'keep';

if (!customElements.get('search-results')) {
  customElements.define(
    'search-results',
    class SearchResults extends HTMLElement {
      constructor() {
        super();
        this.abortController = undefined;
        this.countController = undefined;
        this.countedQuery = undefined;
        this.counts = undefined;
        this.debounceTimer = undefined;
        this.isLoadingMore = false;
        this.onClick = this.onClick.bind(this);
        this.onInput = this.onInput.bind(this);
        this.onPopState = this.onPopState.bind(this);
      }

      connectedCallback() {
        this.sectionId = this.dataset.sectionId;
        this.searchUrl = this.dataset.searchUrl || '/search';
        this.liveSearch = this.hasAttribute('data-live-search');
        this.liveDelay = Number(this.dataset.liveSearchDelay) || 400;
        /* Theme settings → Search → Matching; see assets/component-search-query.js. */
        this.keywordMode = this.dataset.searchMode !== 'broad';

        this.addEventListener('click', this.onClick);
        window.addEventListener('popstate', this.onPopState);

        if (this.liveSearch) {
          this.input = this.querySelector('[data-search-input]');
          this.input?.addEventListener('input', this.onInput);
        }

        this.fillCounts();

        if (this.needsRerun(window.location.href, this)) {
          this.render(this.rerunUrl(window.location.href, this), HISTORY_REPLACE);
        }
      }

      /**
       * sections/search.liquid marks a render that has to be run again:
       * `data-search-broaden` — a quoted query found nothing, so it is rerun as
       * typed and a typo still gets Shopify's fuzzy matches;
       * `data-search-retype` — the search included types this section has
       * switched off, so it is rerun on the enabled ones.
       * A query that arrived unquoted (an old bookmark, a search engine) is
       * rerun quoted, the way the theme's own forms send it, unless LOOSE_PARAM
       * says it is already the broadened rerun of a quoted miss.
       * @param {string} url - the URL that was rendered
       * @param {Element} element - the rendered <search-results>
       * @returns {boolean}
       */
      needsRerun(url, element) {
        if (element.hasAttribute('data-search-broaden') || element.hasAttribute('data-search-retype')) {
          return true;
        }

        const params = new URL(url, window.location.origin).searchParams;
        const query = params.get('q');
        return this.keywordMode && Boolean(query) && !isStrictQuery(query) && !params.has(LOOSE_PARAM);
      }

      /**
       * @param {string} url
       * @param {Element} element - the rendered <search-results> carrying the marks
       * @returns {string}
       */
      rerunUrl(url, element) {
        const target = new URL(url, window.location.origin);
        const query = target.searchParams.get('q');
        if (element.hasAttribute('data-search-broaden')) {
          target.searchParams.set('q', looseQuery(query));
          target.searchParams.set(LOOSE_PARAM, '1');
        } else if (this.keywordMode && query && !isStrictQuery(query) && !target.searchParams.has(LOOSE_PARAM)) {
          target.searchParams.set('q', strictQuery(query));
        }
        if (element.hasAttribute('data-search-retype') && element.dataset.searchTypes) {
          target.searchParams.set('type', element.dataset.searchTypes);
        }
        return target.toString();
      }

      disconnectedCallback() {
        this.removeEventListener('click', this.onClick);
        window.removeEventListener('popstate', this.onPopState);
        this.input?.removeEventListener('input', this.onInput);
        clearTimeout(this.debounceTimer);
        this.abortController?.abort();
        this.countController?.abort();
      }

      /**
       * Asks templates/search.count.liquid for one exact total per type and
       * fills every `[data-count-for]` slot with it.
       *
       * The totals are cached against the query, so a tab swap re-labels the
       * new slots from cache and only a changed query costs requests. A slot
       * whose request failed stays hidden — a count is never guessed.
       */
      async fillCounts(url) {
        const slots = [...this.querySelectorAll('[data-count-for]')];
        if (slots.length === 0) {
          return;
        }

        const source = url || new URL(window.location.href);
        const terms = source.searchParams.get('q');
        if (!terms) {
          return;
        }

        if (this.countedQuery !== terms) {
          this.countController?.abort();
          this.countController = new AbortController();
          const signal = this.countController.signal;
          const totals = await Promise.all(COUNT_TYPES.map((type) => this.fetchCount(type, terms, signal)));

          if (signal.aborted) {
            return;
          }
          this.counts = totals;
          this.countedQuery = terms;
        }

        this.applyCounts(slots);
      }

      applyCounts(slots) {
        COUNT_TYPES.forEach((type, index) => {
          const total = this.counts?.[index];
          if (total === null || total === undefined) {
            return;
          }
          slots
            .filter((slot) => slot.dataset.countFor === type)
            .forEach((slot) => {
              const template = slot.dataset.countTemplate;
              slot.textContent = template ? template.replace(COUNT_TOKEN, total) : `(${total})`;
              slot.hidden = false;
            });
        });
      }

      async fetchCount(type, terms, signal) {
        try {
          const target = new URL(this.searchUrl, window.location.origin);
          target.searchParams.set('q', terms);
          target.searchParams.set('type', type);
          target.searchParams.set('options[prefix]', 'last');
          target.searchParams.set('view', 'count');

          const requestOptions = {
            signal,
          };
          const response = await fetch(target.toString(), requestOptions);
          if (!response.ok) {
            return null;
          }

          const total = parseInt((await response.text()).trim(), 10);
          return Number.isNaN(total) ? null : total;
        } catch (error) {
          return null;
        }
      }

      onClick(event) {
        const tab = event.target.closest('[data-search-tab]');
        if (tab) {
          event.preventDefault();
          this.render(tab.href, HISTORY_PUSH);
          return;
        }

        const loadMore = event.target.closest('[data-load-more]');
        if (loadMore) {
          event.preventDefault();
          this.loadMore(loadMore);
          return;
        }

        const clear = event.target.closest('[data-search-clear]');
        if (clear && this.liveSearch) {
          event.preventDefault();
          if (this.input) {
            this.input.value = '';
          }
          this.input?.focus();
          this.render(this.searchUrl, HISTORY_PUSH);
        }
      }

      onInput() {
        const terms = this.input.value.trim();
        if (terms.length > 0 && terms.length < MIN_QUERY_LENGTH) {
          return;
        }

        clearTimeout(this.debounceTimer);
        this.debounceTimer = setTimeout(() => {
          this.render(this.buildQueryUrl(terms), HISTORY_REPLACE);
        }, this.liveDelay);
      }

      /**
       * A refine keeps the tab the visitor is on, so the type parameter is read
       * back off the current URL rather than held in the DOM where a swap could
       * leave it stale.
       */
      buildQueryUrl(terms) {
        const current = new URL(window.location.href);
        const target = new URL(this.searchUrl, window.location.origin);
        target.searchParams.set('q', this.keywordMode ? strictQuery(terms) : terms);
        target.searchParams.set('options[prefix]', 'last');

        const type = current.searchParams.get('type');
        if (type) {
          target.searchParams.set('type', type);
        }

        return target.toString();
      }

      onPopState() {
        this.render(window.location.href, HISTORY_KEEP);
      }

      async render(url, historyMode) {
        const results = this.querySelector('[data-search-results]');
        this.abortController?.abort();
        this.abortController = new AbortController();
        results?.setAttribute('aria-busy', 'true');

        try {
          const target = new URL(url, window.location.origin);
          target.searchParams.set('section_id', this.sectionId);

          const requestOptions = {
            signal: this.abortController.signal,
          };
          const response = await fetch(target.toString(), requestOptions);
          if (!response.ok) {
            throw new Error(`Search request failed: ${response.status}`);
          }

          const html = new DOMParser().parseFromString(await response.text(), 'text/html');
          const rendered = html.querySelector('search-results');
          if (rendered && this.needsRerun(url, rendered)) {
            this.render(this.rerunUrl(url, rendered), historyMode === HISTORY_KEEP ? HISTORY_REPLACE : historyMode);
            return;
          }
          this.swap(html);
          this.syncMeta(html);

          const pushed = new URL(url, window.location.origin);
          pushed.searchParams.delete('section_id');
          if (historyMode === HISTORY_PUSH) {
            window.history.pushState({}, '', pushed.toString());
          }
          if (historyMode === HISTORY_REPLACE) {
            window.history.replaceState({}, '', pushed.toString());
          }

          /* Counts are keyed off the query, so this has to run against the URL
             just rendered — reading window.location before the history update
             re-applied the previous query's cached numbers. */
          this.fillCounts(pushed);
        } catch (error) {
          if (error.name === 'AbortError') {
            return;
          }

          /* A failed fetch must not leave the visitor on stale results, so fall
             back to the navigation this click would have done unenhanced. */
          window.location.assign(url);
        } finally {
          results?.removeAttribute('aria-busy');
        }
      }

      swap(html) {
        SWAP_TARGETS.forEach((selector) => {
          const next = html.querySelector(selector);
          const current = this.querySelector(selector);
          if (next && current) {
            current.innerHTML = next.innerHTML;
          }
        });
      }

      /**
       * The meta line exists only once a query has run, so it has to be created
       * and removed rather than swapped in place.
       */
      syncMeta(html) {
        const next = html.querySelector('[data-search-meta]');
        const current = this.querySelector('[data-search-meta]');

        if (next && current) {
          current.innerHTML = next.innerHTML;
          return;
        }
        if (next && !current) {
          this.querySelector('[data-search-form]')?.insertAdjacentElement('afterend', next.cloneNode(true));
          return;
        }
        if (!next && current) {
          current.remove();
        }
      }

      /**
       * The range label lives above the results rather than inside
       * [data-search-more], so appending a page does not replace it. Appended
       * results always start at the first one, so only the upper bound moves.
       */
      syncRange(shown) {
        const label = this.querySelector('[data-search-range]');
        const template = label?.dataset.rangeTemplate;
        if (!label || !template) {
          return;
        }
        label.textContent = template.replace(RANGE_TOKEN, shown);
      }

      async loadMore(button) {
        if (this.isLoadingMore) {
          return;
        }
        this.isLoadingMore = true;

        const label = button.textContent;
        button.setAttribute('aria-busy', 'true');
        button.textContent = button.dataset.loadingLabel || label;

        try {
          const target = new URL(button.href, window.location.origin);
          target.searchParams.set('section_id', this.sectionId);

          const response = await fetch(target.toString());
          if (!response.ok) {
            throw new Error(`Load more failed: ${response.status}`);
          }

          const html = new DOMParser().parseFromString(await response.text(), 'text/html');
          this.append(html);

          const pushed = new URL(button.href, window.location.origin);
          pushed.searchParams.delete('section_id');
          window.history.replaceState({}, '', pushed.toString());
        } catch (error) {
          if (error.name !== 'AbortError') {
            window.location.assign(button.href);
          }
        } finally {
          button.removeAttribute('aria-busy');
          button.textContent = label;
          this.isLoadingMore = false;
        }
      }

      append(html) {
        const selector = RESULT_CONTAINERS.find((candidate) => this.querySelector(candidate));
        if (!selector) {
          return;
        }

        const nextItems = html.querySelector(selector);
        const currentItems = this.querySelector(selector);
        if (nextItems && currentItems) {
          currentItems.append(...nextItems.children);
          this.syncRange(currentItems.children.length);
        }

        const nextMore = html.querySelector('[data-search-more]');
        const currentMore = this.querySelector('[data-search-more]');
        if (currentMore) {
          if (nextMore) {
            currentMore.innerHTML = nextMore.innerHTML;
          } else {
            currentMore.remove();
          }
        }
      }
    },
  );
}
